import { isMatch, isValid, parse, parseISO } from 'date-fns';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import 'intl-pluralrules';
import { initReactI18next } from 'react-i18next/initReactI18next';

import { i18nConfig } from '@sola/schema/lib/i18n-config.js';
import { endsWithVowel } from '@sola/utils/koreanGrammar.js';

import { LOCALE_FORMATTERS, isLocaleFormat } from './formatters';

const stripEmptyDigits = (value: number, lang?: string) => {
  const amount =
    Math.floor(value) === value
      ? Intl.NumberFormat(lang, {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(value)
      : Intl.NumberFormat(lang, {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        }).format(value);
  return amount;
};

const formatCurrency = (
  value: number,
  lang?: string,
  options?: {
    useNegativeParens?: boolean;
  },
) => {
  const amount = +value;
  if (Number.isNaN(amount)) {
    return '';
  }

  const { useNegativeParens } = options || {};
  const isNegative = amount < 0;

  let formattedAmount = Intl.NumberFormat(lang, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

  if (useNegativeParens && isNegative) {
    formattedAmount = `(${formattedAmount.replace('-', '')})`;
  }

  return formattedAmount;
};

/**
 * Parses a date string and prepares a Date object
 */
const parseDate = (value: string, preserveCalendarDate?: boolean) => {
  // Preserves a calendar date by creating a UTC date for that day.
  if (preserveCalendarDate) {
    const datePart = value.slice(0, 10);
    if (isMatch(datePart, 'yyyy-MM-dd')) {
      const parsedDate = parse(datePart, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        return new Date(
          Date.UTC(
            parsedDate.getFullYear(),
            parsedDate.getMonth(),
            parsedDate.getDate(),
          ),
        );
      }
    }
  }

  const parsedIsoDate = parseISO(value);
  if (isValid(parsedIsoDate)) {
    return parsedIsoDate;
  }

  return new Date(value);
};

/**
 * Formats a date value as localized long date text.
 * Optionally allows for fixed calendar dates to avoid timezone-based day shifts.
 */
const formatDateLong = (
  value: Date | string,
  lang?: string,
  options?: {
    preserveCalendarDate?: boolean;
    timeZone?: string;
  },
) => {
  const date =
    value instanceof Date
      ? value
      : parseDate(value, options?.preserveCalendarDate);
  // Seems safest to return empty string in case things get wonky.
  if (!isValid(date)) {
    return '';
  }

  // For calendar dates, we don't want to localize.
  const timeZone = options?.preserveCalendarDate
    ? options.timeZone || 'UTC'
    : options?.timeZone;
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(timeZone && { timeZone }),
  };
  return date.toLocaleDateString(lang, dateOptions);
};

const formatDateYearless = (value: Date | string, lang?: string) => {
  const date = value instanceof Date ? value : parseDate(value, true);
  if (!isValid(date)) {
    return '';
  }

  return date.toLocaleDateString(lang, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

const parseFormatOptions = (format: string) => {
  const match = format.match(/^([\w-]+)(?:\((.+)\))?$/);
  if (!match) {
    return { name: format, formatOptions: {} };
  }

  const [, name, optionsStr] = match;
  const formatOptions: Record<string, string> = {};

  if (optionsStr) {
    optionsStr.split(';').forEach((opt) => {
      const [key, val] = opt.split(':').map((s) => s.trim());
      if (key && val) {
        formatOptions[key] = val;
      }
    });
  }

  return { name, formatOptions };
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`./${language}/${namespace}.ts`),
    ),
  )
  .init({
    cleanCode: true,
    debug: false,
    defaultNS: 'common',
    fallbackLng: i18nConfig.defaultLocale,
    fallbackNS: 'common',
    nonExplicitSupportedLngs: true,
    ns: ['priority'],
    partialBundledLanguages: true,
    preload: [i18nConfig.defaultLocale],
    supportedLngs: i18nConfig.locales,
    interpolation: {
      // TODO: check with Kevin & Chris if we can do this
      // escapeValue: false, // Set to false to prevent i18next from escaping HTML entities
      format: (value, format, lang, options) => {
        if (!format) {
          return value;
        }

        const { name, formatOptions } = parseFormatOptions(format);

        const formatters = {
          [LOCALE_FORMATTERS.CURRENCY]: () =>
            // @ts-expect-error - this will be refactored in upcoming MR
            formatCurrency(value, lang, options),
          [LOCALE_FORMATTERS.CURRENCY_CUSTOM]: () =>
            stripEmptyDigits(value, lang),
          [LOCALE_FORMATTERS.DATE_LONG]: () =>
            formatDateLong(value, lang, { timeZone: formatOptions.timeZone }),
          [LOCALE_FORMATTERS.DATE_YEARLESS]: () =>
            formatDateYearless(value, lang),
          [LOCALE_FORMATTERS.VOWEL_ENDING]: () => endsWithVowel(value),
        };

        return isLocaleFormat(name) ? formatters[name]() : value;
      },
    },
  });

// TODO Save this for larger MR>
// SOLA_FORMATTERS.forEach(([name, cb]) => {
//   i18n.services?.formatter?.add(name, cb);
// });
export default i18n;
